/**
 * Test Evidence API service — reads the S3-backed test-run store for the dashboard.
 * Types from the OpenAPI-generated client (src/api/generated/test-evidence.ts).
 * Auth: same Bearer JWT as the rest of the app (shared axios client).
 *
 * Note the list shape: GET /runs returns data = { runs: [], page, limit, total }
 * (paginated object), unlike the manpower lists that put the array directly in data.
 */
import { apiService } from './api';
import type { components } from '../api/generated/test-evidence';

export type RunListItem = components['schemas']['RunListItem'];
export type RunDetail = components['schemas']['RunDetail'];

export interface RunListResult {
	runs: RunListItem[];
	page: number;
	limit: number;
	total: number;
}

export interface ListRunsParams {
	module?: string;
	environment?: 'local' | 'pr' | 'staging';
	prOnly?: boolean;
	page?: number;
	limit?: number;
}

export class TestEvidenceApiService {
	/** GET /test-evidence/runs — paginated, newest first. */
	static async listRuns(params: ListRunsParams = {}): Promise<RunListResult> {
		const sp = new URLSearchParams();
		if (params.module) sp.set('module', params.module);
		if (params.environment) sp.set('environment', params.environment);
		if (params.prOnly) sp.set('prOnly', 'true');
		if (params.page) sp.set('page', String(params.page));
		if (params.limit) sp.set('limit', String(params.limit));
		const q = sp.toString();
		const res = await apiService.get<{ data: RunListResult }>(
			`/test-evidence/runs${q ? `?${q}` : ''}`
		);
		const data = (res as unknown as { data?: RunListResult }).data;
		return data ?? { runs: [], page: 1, limit: 20, total: 0 };
	}

	/** GET /test-evidence/runs/{id} — one run + its screenshots (with presigned view_urls). */
	static async getRun(runId: number): Promise<RunDetail> {
		const res = await apiService.get<{ data: RunDetail }>(`/test-evidence/runs/${runId}`);
		return (res as unknown as { data: RunDetail }).data;
	}
}
